/* eslint-disable no-underscore-dangle */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { Line } from "react-chartjs-2";
import _ from "lodash";
import Chart from "chart.js";
import annotation from "chartjs-plugin-annotation";
import { getROI, nFormatter } from "lib/utils";
import { makeStyles, useMediaQuery, Typography } from "@material-ui/core";
import { BsArrowUp } from "react-icons/bs";
import CustomTooltip from "app/common/chart/CustomTooltipComponent";
import ConfirmationDialog from "app/common/dialog/ConfirmationDialog";
import ScenarioTotalsCardComponent from "app/scenarios/map/ScenarioTotalsCardComponent";
import { PriceOut, SellOut } from "resources/constants";
import Cookies from "universal-cookie";
// import { getEstimationElasticity } from "lib/utils/estimations";

Chart.plugins.register(annotation);

const useStyles = makeStyles((theme) => ({
  chartContainer: {
    height: "100%",
    width: "100%",
  },
  priceTooltipContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
  },
  priceTooltipInfo: {
    width: "100px",
    alignItems: "center",
    display: "flex",
  },
  spaceBetweenPrice: {
    marginRight: "3px",
    fontSize: "13px",
  },
  labelPriceTooltip: {
    marginBottom: "1px",
  },
  percentageColor: {
    color: "#a2a2a2",
    fontSize: "13px",
  },
  totalItemPercentage: {
    display: "flex",
    flexDirection: "row",
    padding: "0px 10px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "12px",
    },
  },
}));

const cookies = new Cookies();

export default function EstimationCyclePromotionsChartComponent({
  selectedRowByCycle,
  selectedPriceState,
  setSelectedPrice,
  setIsPriceChange,
  isReference,
  // eslint-disable-next-line
  groupBy,
  tupleEstimationsByCycle,
  referenceRow,
  currentPromotionalEvent,
  eventLabelHoverChart,
}) {
  const classes = useStyles();
  const chartRef = useRef(null);
  const wrapperRef = useRef(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [tooltipProps, setTooltipProps] = useState({});
  const [chartData, setChartData] = useState({});
  const metadataSlice = useSelector((state) => state.metadata);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const scenariosSlice = useSelector((state) => state.scenarios);
  const user = useSelector((state) => state.auth.user);
  const [isPriceHover, setIsPriceHover] = useState(false);
  const configCatSlice = useSelector((state) => state.auth.configCatUser);
  const sellOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? SellOut
    : "Sell Out";
  const priceOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? PriceOut
    : "Price Out";

  const [sortedEstimations, setSortedEstimations] = useState(
    _.sortBy(tupleEstimationsByCycle, (estimation) => estimation.price)
  );

  const [clickedPrice, setClickedPrice] = useState(selectedRowByCycle.price);

  const getDeltaEstimation = (estimations) => {
    const valuesToUpdate = ["margin", "units", "units_in", "sales"];
    const updatedEstimation = estimations;
    _.each(estimations, (value, index) => {
      if (selectedRowByCycle.price !== value.price) {
        const updateValue = {};
        _.each(valuesToUpdate, (column) => {
          updateValue[column] = value[column] + value[`delta_${column}`];
        });
        updatedEstimation[index] = { ...value, ...updateValue };
      }
    });

    return updatedEstimation;
  };

  const setPositionAndData = (payload) => {
    setTooltipProps({
      top: payload.top,
      left: payload.left,
      position: "absolute",
      tooltipModel: payload.tooltipModel,
      showTooltip: true,
      width: "220px",
    });
  };

  const hide = () => {
    setTooltipProps({
      ...tooltipProps,
      showTooltip: false,
    });
  };

  const updateChartData = (price) => {
    const chart = chartRef.current.chartInstance;

    if (_.isEmpty(chart.data.datasets)) return;

    const pointRadiusArray = _.map(sortedEstimations, (estimation) => {
      if (price === estimation.price) return 5;
      return 3;
    });

    chart.data.datasets[0].pointRadius = [...pointRadiusArray];

    chart.data.datasets[1].pointRadius = [...pointRadiusArray];

    const referenceByDateOrAggregated = referenceRow.price;

    chart.options.annotation = {
      annotations: [
        {
          drawTime: "afterDraw",
          type: "line",
          mode: "vertical",
          scaleID: "Price",
          value: isReference ? price : referenceByDateOrAggregated,
          borderColor: "#CCCCCC",
          borderWidth: 2,
          borderDash: [4, 4],
          borderDashOffset: 5,
        },
        {
          drawTime: "afterDraw",
          type: "line",
          mode: "vertical",
          scaleID: "Price",
          value: price,
          borderColor: "#F8AB10",
          borderWidth: 2,
          borderDash: [4, 4],
          borderDashOffset: 5,
        },
      ],
    };

    chart.update();
  };

  const updatePrice = (price) => {
    updateChartData(price);
    setSelectedPrice(price);
    setIsPriceChange(true);
  };

  const onConfirmAction = (e, confirm) => {
    e.stopPropagation();
    if (checkboxChecked) {
      cookies.set(
        "cycleIdByUserSelected",
        JSON.stringify({
          ...cookies.get("cycleIdByUserSelected"),
          [`${user.username}-${scenariosSlice.selectedScenario.scenario.cycle.id}`]: true,
        }),
        { path: "/" }
      );
    }
    if (confirm) {
      updatePrice(clickedPrice);
    }
    setConfirmDialogOpen(false);
    setCheckboxChecked(false);
  };

  const chartDataUpdate = (
    auxSortedEstimations,
    pointRadiusArray,
    auxSortedEstimationsWithReference
  ) => {
    return {
      labels: _.map(
        !isReference ? auxSortedEstimationsWithReference : auxSortedEstimations,
        (estimation) => estimation.price
      ),
      datasets: [
        {
          label: sellOutLabelByTenant,
          yAxisID: "units",
          fill: false,
          borderColor: "#47B5DB",
          pointBackgroundColor: "white",
          pointHoverRadius: 5,
          pointHoverBorderWidth: 2,
          hoverBackgroundColor: "white",
          pointHitRadius: 5,
          pointRadius: pointRadiusArray,
          data: _.map(auxSortedEstimations, (correctedEstimation) => {
            return {
              ...correctedEstimation,
              title: `Price: ${correctedEstimation.price}`,
              borderStyle: { borderColor: "#181E2A" },
              x: correctedEstimation.price,
              y: correctedEstimation.units,
              units: {
                absolute: correctedEstimation.units,
                percentage:
                  (100 * (correctedEstimation.units - referenceRow.units)) /
                  Math.abs(referenceRow.units),
              },
              units_in: {
                absolute: correctedEstimation.units_in,
                percentage:
                  (100 *
                    (correctedEstimation.units_in - referenceRow.units_in)) /
                  Math.abs(referenceRow.units_in),
              },
              margin: {
                absolute: correctedEstimation.margin,
                percentage:
                  (100 * (correctedEstimation.margin - referenceRow.margin)) /
                  Math.abs(referenceRow.margin),
              },
              sales: {
                absolute: correctedEstimation.sales,
                percentage:
                  (100 * (correctedEstimation.sales - referenceRow.sales)) /
                  Math.abs(referenceRow.sales),
              },
            };
          }),
        },
        {
          label: "Margin",
          yAxisID: "Margin",
          fill: false,
          borderColor: "#66AE91",
          pointBackgroundColor: "white",
          pointHoverRadius: 5,
          pointHoverBorderWidth: 2,
          hoverBackgroundColor: "white",
          pointHitRadius: 5,
          pointRadius: pointRadiusArray,
          data: _.map(auxSortedEstimations, (correctedEstimation) => {
            return {
              ...correctedEstimation,
              title: `Price: ${correctedEstimation.price}`,
              color: "#545455",
              x: correctedEstimation.price,
              y: correctedEstimation.margin,
              units: {
                absolute: correctedEstimation.units,
                percentage:
                  (100 * (correctedEstimation.units - referenceRow.units)) /
                  Math.abs(referenceRow.units),
              },
              units_in: {
                absolute: correctedEstimation.units_in,
                percentage:
                  (100 *
                    (correctedEstimation.units_in - referenceRow.units_in)) /
                  Math.abs(referenceRow.units_in),
              },
              margin: {
                absolute: correctedEstimation.margin,
                percentage:
                  (100 * (correctedEstimation.margin - referenceRow.margin)) /
                  Math.abs(referenceRow.margin),
              },
              sales: {
                absolute: correctedEstimation.sales,
                percentage:
                  (100 * (correctedEstimation.sales - referenceRow.sales)) /
                  Math.abs(referenceRow.sales),
              },
            };
          }),
        },
      ],
    };
  };

  const handlePriceClick = (price) => {
    const isCycleIgnoredByUser = cookies.get("cycleIdByUserSelected")?.[
      `${user.username}-${scenariosSlice.selectedScenario.scenario.cycle.id}`
    ];
    setClickedPrice(price);
    if (isReference && !isCycleIgnoredByUser) setConfirmDialogOpen(true);
    else updatePrice(price);
  };

  useEffect(() => {
    let isSelectedReferenceRowPriceInSortedEstimations;
    // When we select a pricing strategy by product, product-retailer or product-area the price of the row is a weighted average and may not coincide with any of the price points retrieved from the server. That is why we have to append it to the tupleEstimations if a match is not found
    const isSelectedRowPriceInSortedEstimations = _.find(
      sortedEstimations,
      (estimation) => estimation.price === selectedRowByCycle.price
    );

    const auxSortedEstimations = !isSelectedRowPriceInSortedEstimations
      ? // The following line adds the selected row to the estimations, it migth be a weighted price
        _.sortBy(
          [selectedRowByCycle, ...tupleEstimationsByCycle],
          (estimation) => estimation.price
        )
      : // The following line will take out the row of the selected price, and append the selected row
        _.sortBy(
          [
            ..._.map(tupleEstimationsByCycle, (estimation) => {
              if (estimation.price === selectedRowByCycle.price)
                return { ...estimation, ...selectedRowByCycle };
              return estimation;
            }),
          ],
          (estimation) => estimation.price
        );

    let auxSortedEstimationsWithReference = null;

    if (!isReference) {
      isSelectedReferenceRowPriceInSortedEstimations = _.find(
        auxSortedEstimations,
        (estimation) => estimation.price === referenceRow.price
      );
      const localReferenceRow = referenceRow;
      auxSortedEstimationsWithReference = !isSelectedReferenceRowPriceInSortedEstimations
        ? _.sortBy(
            [
              localReferenceRow.price !== selectedRowByCycle.price
                ? localReferenceRow
                : selectedRowByCycle,
              ...auxSortedEstimations,
            ],
            (estimation) => estimation.price
          )
        : _.sortBy(
            [
              ..._.filter(auxSortedEstimations, (estimation) => {
                return estimation.price !== localReferenceRow.price;
              }),
              localReferenceRow.price !== selectedRowByCycle.price
                ? localReferenceRow
                : selectedRowByCycle,
            ],
            (estimation) => estimation.price
          );
    }

    setSortedEstimations(auxSortedEstimations);

    const pointRadiusArray = _.map(auxSortedEstimations, (estimation) => {
      if (selectedRowByCycle.price === estimation.price) return 5;
      return 3;
    });

    // const correctionEstimationArray = adjustCorrection(
    //   auxSortedEstimations,
    //   isSelectedRowPriceInSortedEstimations
    //   // isSelectedReferenceRowPriceInSortedEstimations
    // );

    setChartData(
      chartDataUpdate(
        currentPromotionalEvent.key !== null
          ? getDeltaEstimation(auxSortedEstimations)
          : auxSortedEstimations,
        pointRadiusArray,
        auxSortedEstimationsWithReference
      )
    );
  }, [tupleEstimationsByCycle, selectedRowByCycle, currentPromotionalEvent]);

  useEffect(() => {
    updateChartData(selectedRowByCycle.price);
  }, [referenceRow, chartData]);

  useEffect(() => {
    const chart = chartRef.current.chartInstance;
    if (_.isEmpty(chart.data.datasets)) return;

    const pointRadiusArray = _.map(sortedEstimations, (estimation) => {
      if (selectedRowByCycle.price === estimation.price) return 5;
      return 3;
    });

    chart.data.datasets[0].pointRadius = [...pointRadiusArray];

    chart.data.datasets[1].pointRadius = [...pointRadiusArray];

    chart.update();
  }, [sortedEstimations, referenceRow]);

  const chartOptions = {
    maintainAspectRatio: false,
    legend: {
      display: false,
    },
    layout: {
      padding: {
        left: 50,
        right: 70,
        top: 20,
        bottom: 20,
      },
    },
    animation: false,
    tooltips: {
      enabled: false,
      intersect: isSmallScreen,
      mode: "index",
      custom: useCallback((tooltipModel) => {
        if (tooltipModel.opacity === 0) {
          hide();
          return;
        }

        const chart = chartRef.current.chartInstance;
        const { canvas } = chart.ctx;

        if (canvas) {
          // set position of tooltip

          const valueTopTpo = 0;

          const left =
            tooltipModel.caretX <= chart.width / 2
              ? tooltipModel.caretX + 60
              : tooltipModel.caretX - 280;
          const top = tooltipModel.caretY + valueTopTpo;

          setPositionAndData({ top, left, tooltipModel });
        }
      }, []),
    },
    onHover: useCallback((event, chartElement) => {
      const newEvent = event;
      if (!chartElement[0]) newEvent.target.style.cursor = "default";
      else newEvent.target.style.cursor = "pointer";
      const chart = chartRef.current.chartInstance;
      const datasetIndex = 0;
      const activeTooltip = chart.tooltip._active
        ? chart.tooltip._active[datasetIndex]
        : null;
      if (activeTooltip) {
        // We are checking if the active index has an appropiate lenght because when it is the last hover and we have in the dataset an average price that doesn't exist we need to get the previous value
        const selectedPrice =
          chart.data.datasets[datasetIndex].data[
            activeTooltip._index >
            chart.data.datasets[datasetIndex].data.length - 1
              ? activeTooltip._index - 1
              : activeTooltip._index
          ].price;
        const isPrice = _.filter(sortedEstimations, (estimations) => {
          return estimations.price === selectedPrice;
        });

        if (isPrice.length === 0 && selectedPrice) {
          setIsPriceHover(true);
          return;
        }
        setIsPriceHover(false);
      }
    }, []),
    onClick: configCatSlice?.features?.IsMaintenanceActive
      ? undefined
      : useCallback((evt) => {
          const chart = chartRef.current.chartInstance;
          const datasetIndex = 0;
          const activeTooltip = chart.tooltip._active[datasetIndex];
          const selectedPrice =
            chart.data.datasets[datasetIndex].data[activeTooltip._index].price;

          const isPrice = _.filter(sortedEstimations, (estimations) => {
            return estimations.price === selectedPrice;
          });

          if (isPrice.length === 0) {
            setIsPriceHover(true);
            return;
          }
          setIsPriceHover(false);
          /**
           * To improve usability, we first take the price contained in the tooltip
           * shown to the user, so that it does not select a price that the user is
           * not aware of
           */
          if (activeTooltip) {
            handlePriceClick(
              chart.data.datasets[datasetIndex].data[activeTooltip._index].price
            );
          } else {
            // No tooltip active, get data from clicked data point
            const element = chart.getElementAtEvent(evt);

            if (element.length)
              handlePriceClick(
                sortedEstimations[element[datasetIndex]._index].price
              );
          }
        }, []),
    scales: {
      yAxes: [
        {
          id: "units",
          scaleLabel: {
            labelString: sellOutLabelByTenant,
            fontStyle: "bold",
            display: false,
          },
          gridLines: {
            drawOnChartArea: false,
            drawTicks: false,
            color: "#A9A9A9",
            lineWidth: 2,
          },
          ticks: {
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 5,
            maxRotation: 0,
            minRotation: 0,
            callback: React.useCallback((value) => {
              return `${nFormatter(value)}`;
            }, []),
          },
          position: "left",
        },
        {
          id: "Margin",
          // scaleLabel: {
          //   labelString: `Margin`,
          //   fontStyle: "bold",
          //   display: true,
          // },
          gridLines: {
            drawOnChartArea: false,
            drawTicks: false,
            color: "#A9A9A9",
            lineWidth: 2,
          },
          ticks: {
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 5,
            maxRotation: 0,
            minRotation: 0,
            callback: React.useCallback((value) => {
              return `${nFormatter(value)}`;
            }, []),
          },
          position: "right",
        },
      ],
      xAxes: [
        {
          id: "Price",
          scaleLabel: {
            labelString: priceOutLabelByTenant,
            display: false,
            fontStyle: "bold",
          },
          gridLines: {
            drawOnChartArea: false,
            drawTicks: false,
            color: "#F8AB10",
            lineWidth: 1,
          },
          ticks: {
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 5,
            maxRotation: 0,
            minRotation: 0,
            callback: React.useCallback((value) => {
              return `${nFormatter(value)}`;
            }, []),
          },
          position: "bottom",
        },
      ],
    },
  };

  const ROI = () => {
    const rowValues =
      chartRef.current.chartInstance.data.datasets[
        tooltipProps.tooltipModel.dataPoints[0].datasetIndex
      ].data[
        tooltipProps.tooltipModel.dataPoints[0].index >
        chartRef.current.chartInstance.data.datasets[
          tooltipProps.tooltipModel.dataPoints[0].datasetIndex
        ].data.length -
          1
          ? tooltipProps.tooltipModel.dataPoints[0].index - 1
          : tooltipProps.tooltipModel.dataPoints[0].index
      ];

    let refPriceIn = referenceRow.price_in;
    let estPriceIn = rowValues.price_in;

    const toolTipEstimation = sortedEstimations.find(
      (estimation) => estimation.price === rowValues.price
    );

    if (configCatSlice?.features.newInvestmentCalculation) {
      refPriceIn = referenceRow.price_in_wo_taxes;
      estPriceIn = toolTipEstimation.price_in_wo_taxes;
    }

    const investmentIn = (refPriceIn - estPriceIn) * toolTipEstimation.units_in;

    const correctedROI =
      rowValues?.roi !== 0 && rowValues?.roi !== undefined
        ? rowValues.roi
        : getROI(
            toolTipEstimation.margin,
            currentPromotionalEvent.label === "Multibuy"
              ? referenceRow.margin // TO INVESTIGATE WE PROBABLY SHOULD COMPARE THE VALUES FOR REFERENCE WITH DELTAS ADDED
              : referenceRow.margin,
            investmentIn,
            {
              strategy: configCatSlice?.features.roiStrategy,
              unitsEsc: toolTipEstimation.units,
              unitsRef: referenceRow.units,
            }
          );

    const titleForROIGroupBy = (aggrupation) => {
      if (aggrupation) {
        return `≈ ${correctedROI}`;
      }
      return correctedROI;
    };

    let roiValue = null;
    if (!isReference) {
      if (rowValues.price > referenceRow.price) {
        roiValue = "N/A";
      } else {
        roiValue = titleForROIGroupBy(groupBy);
      }
    }
    return roiValue;
  };

  const getDeltaEvent = () => {
    const deltaEvent = {
      id: currentPromotionalEvent.key,
      promPrice: "NA",
      redemption: 0,
    };
    if (
      currentPromotionalEvent.label === "Multibuy" &&
      tooltipProps.showTooltip
    ) {
      const actualDataPointIndex =
        tooltipProps.tooltipModel.dataPoints[0].index;
      const actualDataSetIndex =
        tooltipProps.tooltipModel.dataPoints[0].datasetIndex;

      const actualPointsData =
        chartRef.current.chartInstance.data.datasets[actualDataSetIndex].data;

      const realPointIndex =
        actualDataPointIndex > actualPointsData.length - 1
          ? actualDataPointIndex - 1
          : actualDataPointIndex;

      // reference price
      const referencePrice = referenceRow.price;
      // price out
      const currentPrice = actualPointsData[realPointIndex].price;
      // prom price
      let promPrice = actualPointsData[realPointIndex].event_price || 0;
      if (currentPrice <= referencePrice) {
        // if (configCatSlice?.features.newRedemptionCalculation) {
        // slope
        const slope = actualPointsData[realPointIndex].delta_units_slope || 0;
        // intercept
        const intercept =
          actualPointsData[realPointIndex].delta_units_intercept || 0;
        // Quadratic formula implementation
        const leftTopSideOperation = (intercept / slope) * referencePrice;
        const rightTopSideOperation = Math.sqrt(
          ((intercept / slope) * referencePrice) ** 2 -
            4 * (referencePrice / slope) * (currentPrice - referencePrice)
        );
        const bottomSideOperation = 2 * (referencePrice / slope);
        // redemption
        let redemption =
          (leftTopSideOperation + rightTopSideOperation) / bottomSideOperation;
        if (redemption > 1) {
          redemption = 1;
        } else if (redemption < 0) {
          redemption = 0;
        }
        // setting promPrice using the redemption
        promPrice =
          (currentPrice - referencePrice * (1 - redemption)) / redemption;
        deltaEvent.redemption = _.round(redemption * 100, 2);
        deltaEvent.promPrice = _.round(promPrice, 2);
        // }
        // else {
        //   deltaEvent.promPrice = promPrice;
        //   deltaEvent.redemption = _.round(
        //     (100 * (referencePrice - currentPrice)) /
        //       (referencePrice - promPrice),
        //     2
        //   );
        // }
      }
    }
    return deltaEvent;
  };

  const getHoverIndex = () => {
    return tooltipProps?.tooltipModel?.dataPoints[0]?.index;
  };
  const getEstimationsByHoverIndex = () => {
    return tupleEstimationsByCycle[getHoverIndex()];
  };
  const getPromPrice = () => {
    return currentPromotionalEvent.label === "Multibuy"
      ? getEstimationsByHoverIndex()?.event_price
      : null;
  };
  const getHoverPrice = () => {
    return getEstimationsByHoverIndex()?.price;
  };

  // const getChartSellOutDataset = () => {
  //   return chartRef.current.chartInstance.data.datasets[0];
  // };
  // const getChartMarginDataset = () => {
  //   return chartRef.current.chartInstance.data.datasets[0];
  // };
  // const getDatasetIndex = () => {
  //   return tooltipProps?.tooltipModel?.dataPoints[0]?.datasetIndex;
  // };

  return (
    <>
      <ConfirmationDialog
        title="Reference Change"
        content="Reference scenario will also change for your teammates. Additionally,
          all the comparisons against it will be updated. Do you want to proceed?"
        open={confirmDialogOpen}
        onConfirmAction={onConfirmAction}
        onCheckboxChecked={setCheckboxChecked}
        isCheckboxComponent
      />

      <div ref={wrapperRef} className={classes.chartContainer}>
        <Line ref={chartRef} data={chartData} options={chartOptions} />
        {tooltipProps.showTooltip ? (
          <CustomTooltip
            roi={ROI()}
            isPriceHover={isPriceHover}
            tooltipProps={tooltipProps}
            tooltipData={{ title: "" }}
            eventLabelHoverChart={eventLabelHoverChart}
            selectedPrice={selectedPriceState}
            metadataUnit={metadataSlice["custom-projection"].price[0].unit}
            promPrice={getPromPrice()}
            referenceRowPrice={referenceRow.price}
            hoverPrice={getHoverPrice()}
            deltaEvent={getDeltaEvent()}
            items={[
              {
                content: (
                  <div>
                    <Typography
                      variant="body2"
                      className={classes.labelPriceTooltip}
                    >
                      Price Out
                    </Typography>
                    <div className={classes.priceTooltipContainer}>
                      <div className={classes.priceTooltipInfo}>
                        <Typography
                          variant="body2"
                          className={classes.spaceBetweenPrice}
                        >
                          {metadataSlice["custom-projection"].price[0].unit}
                        </Typography>
                        <Typography variant="body2">
                          {
                            chartRef.current.chartInstance.data.datasets[
                              tooltipProps.tooltipModel.dataPoints[0]
                                .datasetIndex
                            ].data[
                              tooltipProps.tooltipModel.dataPoints[0].index >
                              chartRef.current.chartInstance.data.datasets[
                                tooltipProps.tooltipModel.dataPoints[0]
                                  .datasetIndex
                              ].data.length -
                                1
                                ? tooltipProps.tooltipModel.dataPoints[0]
                                    .index - 1
                                : tooltipProps.tooltipModel.dataPoints[0].index
                            ].price
                          }
                        </Typography>
                      </div>
                      {!isReference &&
                        Math.abs(
                          ((chartRef.current.chartInstance.data.datasets[
                            tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                          ].data[
                            tooltipProps.tooltipModel.dataPoints[0].index >
                            chartRef.current.chartInstance.data.datasets[
                              tooltipProps.tooltipModel.dataPoints[0]
                                .datasetIndex
                            ].data.length -
                              1
                              ? tooltipProps.tooltipModel.dataPoints[0].index -
                                1
                              : tooltipProps.tooltipModel.dataPoints[0].index
                          ].price -
                            referenceRow.price) *
                            100) /
                            referenceRow.price
                        )
                          .toFixed(2)
                          .toString() !== "0.00" && (
                          <div
                            component="span"
                            className={`${classes.totalItemPercentage} ${classes.percentageColor}`}
                          >
                            <BsArrowUp
                              size={20}
                              strokeWidth={1.5}
                              style={{
                                marginRight: "5px",
                                color: "#a2a2a2",
                                transform: `rotateX(${
                                  chartRef.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .index >
                                    chartRef.current.chartInstance.data
                                      .datasets[
                                      tooltipProps.tooltipModel.dataPoints[0]
                                        .datasetIndex
                                    ].data.length -
                                      1
                                      ? tooltipProps.tooltipModel.dataPoints[0]
                                          .index - 1
                                      : tooltipProps.tooltipModel.dataPoints[0]
                                          .index
                                  ].price -
                                    referenceRow.price >
                                  0
                                    ? "0deg"
                                    : "180deg"
                                }
                                  )`,
                              }}
                            />
                            {Math.abs(
                              ((chartRef.current.chartInstance.data.datasets[
                                tooltipProps.tooltipModel.dataPoints[0]
                                  .datasetIndex
                              ].data[
                                tooltipProps.tooltipModel.dataPoints[0].index >
                                chartRef.current.chartInstance.data.datasets[
                                  tooltipProps.tooltipModel.dataPoints[0]
                                    .datasetIndex
                                ].data.length -
                                  1
                                  ? tooltipProps.tooltipModel.dataPoints[0]
                                      .index - 1
                                  : tooltipProps.tooltipModel.dataPoints[0]
                                      .index
                              ].price -
                                referenceRow.price) *
                                100) /
                                referenceRow.price
                            ).toFixed(2)}
                            %
                          </div>
                        )}
                    </div>
                  </div>
                ),
                borderStyle: {
                  borderColor: "#F8AB10",
                },
              },
            ]}
            body={
              <ScenarioTotalsCardComponent
                cardData={
                  chartRef.current.chartInstance.data.datasets[
                    tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                  ].data[
                    tooltipProps.tooltipModel.dataPoints[0].index >
                    chartRef.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data.length -
                      1
                      ? tooltipProps.tooltipModel.dataPoints[0].index - 1
                      : tooltipProps.tooltipModel.dataPoints[0].index
                  ]
                }
                isReference={isReference}
                metadata={metadataSlice}
                visibleKeys={["units", "margin", "sales"]}
                showLabels
                showInChart
                showIcons={false}
                showLine
                orderWithVisibleKeys
              />
            }
            fixedWidth={false}
          />
        ) : null}
      </div>
    </>
  );
}
